// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
  CssBaseline,
  ThemeProvider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ColorModeContext, useMode } from "./theme";
import { ApolloProvider } from "@apollo/client";
import EnvironmentIndicator from "./components/EnvironmentIndicator";
import {
  validateEnvironment,
  getEnvironmentInfo,
} from "./utils/environmentValidator";
import ChangePasswordForm from "./scenes/users/changepassword";
import Dashboard from "./scenes/dashboard";
import Modules from "./scenes/RoleManagement/Modules";
import Rights from "./scenes/RoleManagement/Rights";
import RoleGroups from "./scenes/RoleManagement/RoleGroup";
import Role from "./scenes/RoleManagement/Roles";
import AssignRight from "./scenes/RoleManagement/Roles/AssignRoleRights";
import Users from "./scenes/users";
import UsersForm from "./scenes/users/users-form";
import { Reduxstore } from "./store/store";
import { Provider } from "react-redux";
// Apollo clients
import {
  createRoleClient,
  createSetupClient,
  createUsersClient,
} from "./config";
import { ApolloClient, InMemoryCache } from "@apollo/client";
import withLayout from "./components/HOCApp";
import BarChart from "./components/BarChart";
import PersonDetails from "./scenes/Profile";
import ForgotPasswordForm from "./scenes/users/ForgotPassword";
import SubRightTable from "./scenes/RoleManagement/SubRight";
import POS from "./scenes/POS";
import AccountAndFinanceModule from "./scenes/AccountsAndFinanceManagement/Accounts";
import CompanyManagementModule from "./scenes/CompanyManagement/CompanyManagement";
import ChartOfAccounts from "./scenes/AccountsAndFinanceManagement/chartofaccounts";
import McbSwitch from "./scenes/login/mcbswitch";

import HotelPOS from "./scenes/POS/HotelPOS";
import ButcheryPOS from "./scenes/POS/butcheryPOS";

import IndividualClients from "./scenes/clientManagement/individualClients";
import Setups from "./scenes/SetupManagement/LeadSetups";
import InventoryTable from "./scenes/InventoryManagement/Inventory";
import WarehouseInventoryTable from "./scenes/InventoryManagement/Inventory/warehouseinventory";
import WarehouseDistributionTable from "./scenes/InventoryManagement/Inventory/warehousedistribution";
import ProductsTable from "./scenes/InventoryManagement/Products";
import SalesOrders from "./scenes/SalesManagement/SalesOders";
import PurchaseOrders from "./scenes/SalesManagement/PurchaseOders";
import VendorsTable from "./scenes/SalesManagement/Vendors";
import Expense from "./scenes/dashboard/components/expenses";
import PromotionsPage from "./scenes/POS/promotions/promotions";

import SignInSide from "./scenes/login/LoginPage";
import FixedExpenses from "./scenes/dashboard/components/FixedExpenses";
import CustomSMSPage from "./scenes/POS/promotions/customSMS";
import EmailsPage from "./scenes/POS/promotions/emails";
import PurchaseOrderTable from "./scenes/SalesManagement/PurchaseOrder";
import SalesOrderTable from "./scenes/SalesManagement/SalesOrder";
import SalesOrderItemsTable from "./scenes/SalesManagement/SalesOrderItems";
import PurchaseOrderItemsTable from "./scenes/SalesManagement/PurchaseOrderItems";
import AdhocMain from "./scenes/adhoc/adhoc";
import MajorClassification from "./scenes/Reports/MajorClassification";
import MinorClassification from "./scenes/Reports/MinorClassification";
import ReportTypes from "./scenes/Reports/ReportTypes";
import ReportBrowser from "./scenes/Reports/ReportBrowser";
import LandingPage from "./scenes/global/LandingPage";
import CustomersCardNew from "./scenes/CompanyManagement/NewJsons/customercardnew";
import DynamicDataTableJson from "./scenes/CompanyManagement/NewJsons/enumerable";
import { SuspendedTransactions } from "./scenes/AccountsAndFinanceManagement/Component/Components";
import CustomersJson from "./scenes/clientManagement/individualClients/customersjson";
import DiscountTypesTable from "./scenes/SetupManagement/DiscountTypes";
import DiscountRulesTable from "./scenes/SalesManagement/DiscountRules";
import BusinessRules from "./scenes/SetupManagement/BusinessRules/businessrules";
import InventoryBranchTransfer from "./scenes/InventoryManagement/Inventory/inventorybranchtransfer";
import SellingPriceRules from "./scenes/SalesManagement/SellingpriceRules";
import PaymentVoucher from "./scenes/SalesManagement/PaymentVoucher";
import ShoeInventoryPOS from "./scenes/POS/ShoeInventoryPOS";
import RetailDistribution from "./scenes/InventoryManagement/Inventory/retaildistribution";
import RetailDistributionTable from "./scenes/SalesManagement/RetailDistribution/retaildistributionlogs";
import OTPVerification from "./scenes/login/OTPVerification";
import ExternalProductsTable from "./scenes/SalesManagement/ExternalProducts";
import SaleOrderPaymentMethod from "./scenes/SalesManagement/PaymentMethod";
import PurchaseOrderPaymentMethod from "./scenes/SalesManagement/POPaymentMethod";
import ReversedSaleorder from "./scenes/SalesManagement/ReversedSaleOrder";
import ReversePurchaseOrders from "./scenes/SalesManagement/ReversedPurchaseOrders";
import PasswordPolicy from "./scenes/users/PasswordPolicy";
import InventoryBulkUpload from "./scenes/InventoryManagement/Inventory/Uploads";
import UserAuditsTable from "./scenes/users/UserAudit";
import InventoryAuditsTable from "./scenes/InventoryManagement/InventoryAudits";
import WarehouseAuditsTable from "./scenes/InventoryManagement/InventoryAudits/WarehouseAudits";
import PaymentVoucherAuditsTable from "./scenes/SalesManagement/PaymentVoucher/PaymentVoucherAudits";
import SalesOrdersAuditsTable from "./scenes/SalesManagement/SalesOrderAudits";
import SalesOrderItemsAuditsTable from "./scenes/SalesManagement/SalesOrderAudits/SalesOrderItemsAudits";
import PurchaseOrdersAuditsTable from "./scenes/SalesManagement/PurchaseOrdersAudits";
import PurchaseOrderItemsAuditsTable from "./scenes/SalesManagement/PurchaseOrdersAudits/PurchaseOrderItemsAudits";
/**
 * The main App component that renders different routes based on the user's authentication status.
 * If the user is authenticated, it renders the main application with different routes.
 * If the user is not authenticated, it renders the login page.
 * The App component also handles the user's inactivity by logging them out after a certain
 * amount of time.
 *
 * @param {boolean} isAuthenticated - Whether the user is authenticated or not.
 * @param {function} setIsAuthenticated - A function to set the isAuthenticated state.
 * @param {ReactRouterDOM.Navigate} navigate - The navigate function from React Router DOM.
 * @param {ReactRouterDOM.Location} location - The location object from React Router DOM.
 * @param {ApolloClient} userManagementClient - The Apollo client for user management.
 * @param {ApolloClient} roleManagementClient - The Apollo client for role management.
 * @param {ApolloClient} setupManagementClient - The Apollo client for setup management.
 * @param {ApolloClient} salesManagementClient - The Apollo client for sales management.
 */
function App() {
  const userManagementClient = createUsersClient();
  const roleManagementClient = createRoleClient();
  const setupManagementClient = createSetupClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, colorMode] = useMode();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showSessionAlert, setShowSessionAlert] = useState(false);

  useEffect(() => {
    const logoutReason = localStorage.getItem("logoutReason");

    if (logoutReason === "session_terminated") {
      localStorage.removeItem("logoutReason");
      setShowSessionAlert(true);
    }
  }, []);
  // Validate environment configuration on startup
  useEffect(() => {
    const validation = validateEnvironment();
    const envInfo = getEnvironmentInfo();


    if (!validation.isValid) {
      console.error("⚠️ ENVIRONMENT VALIDATION FAILED!");
      validation.warnings.forEach((warning) => {
        console.error(`  ❌ ${warning}`);
      });
    } else {

    }
  }, []);
  useEffect(() => {
    /**
     * Checks if the user's token has expired and logs them out if it has.
     * If the user is currently on the login or forgot password page, do nothing.
     * Otherwise, remove the token and expiration time from local storage, set isAuthenticated to false, and navigate to the login page.
     */
    const checkTokenExpiration = () => {
      const token = localStorage.getItem("token");
      const expirationTime = localStorage.getItem("tokenExpiration");
      const currentTime = new Date().getTime();

      if (
        !token ||
        !expirationTime ||
        currentTime > parseInt(expirationTime, 10)
      ) {
        // Token is missing or expired, log out the user
        if (
          location.pathname === "/forgot-password" ||
          location.pathname === "/" ||
          location.pathname === "/signin"
        ) {
          // Don't redirect if already on login pages
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("tokenExpiration");
          setIsAuthenticated(false);
          navigate("/signin");
        }
      }
    };

    const interval = setInterval(checkTokenExpiration, 60000); // Check every minute

    // Initial check when component mounts
    checkTokenExpiration();

    return () => clearInterval(interval);
  }, [navigate, setIsAuthenticated, location.pathname]);

  const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
  useEffect(() => {
    let inactivityTimer;

    /**
     * Resets the inactivity timer. If the timer is already running, it clears the old timeout and sets a new one. If the timer is not running, it sets a new timeout. When the timeout expires, it logs the user out and navigates them to the login page.
     */
    const resetInactivityTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      inactivityTimer = setTimeout(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("tokenExpiration");
        localStorage.clear();
        setIsAuthenticated(false);
        navigate("/signin");
      }, INACTIVITY_LIMIT);
    };

    const handleActivity = () => {
      resetInactivityTimer();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);

    resetInactivityTimer(); // Initialize timer

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    };
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAuthenticated(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      {/* <Provider store={Reduxstore}> */}
      <ThemeProvider theme={theme}>
        <CssBaseline />{" "}
        <Dialog
          open={showSessionAlert}
          onClose={() => setShowSessionAlert(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              bgcolor: "#FFF3E0",
              display: "flex",
              justifyContent: "center",
              pt: 4,
              pb: 2,
            }}
          >
            <WarningAmberRoundedIcon
              sx={{ fontSize: 64, color: "#E65100" }}
            />
          </Box>
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: "1.4rem",
              pb: 0,
            }}
          >
            Session Terminated
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", px: 4, pt: 1 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Your session was ended because your account was signed in from
              another device or browser.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              For security, only one active session is allowed at a time. Please
              sign in again to continue.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3, px: 4 }}>
            <Button
              onClick={() => setShowSessionAlert(false)}
              variant="contained"
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                py: 1.2,
                fontSize: "1rem",
                borderRadius: 2,
              }}
            >
              Sign In Again
            </Button>
          </DialogActions>
        </Dialog>
        <ApolloProvider client={userManagementClient}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/signin"
              element={<SignInSide onLogin={() => setIsAuthenticated(true)} />}
            />
            <Route path="/changepassword" element={<ChangePasswordForm />} />
            <Route path="/forgot-password" element={<ForgotPasswordForm />} />
            <Route path="/profile" element={withLayout(PersonDetails)()} />
            <Route path="/MCB-SWITCH" element={<McbSwitch />} />
            <Route path="/otp-verification" element={<OTPVerification />} />
          </Routes>
        </ApolloProvider>
        {isAuthenticated && (
          <>
            <ApolloProvider client={roleManagementClient}>
              <Routes>
                <Route path="/module" element={withLayout(Modules)()} />
                <Route path="/rights" element={withLayout(Rights)()} />
                <Route path="/role-group" element={withLayout(RoleGroups)()} />
                <Route path="/role" element={withLayout(Role)()} />
                <Route path="/subright" element={withLayout(SubRightTable)()} />
                <Route
                  path="/role-Rights"
                  element={withLayout(AssignRight)()}
                />
                <Route path="/bar" element={withLayout(BarChart)()} />
                <Route
                  path="/super-admin-dashboard"
                  element={withLayout(Dashboard)()}
                />

                {/* Customers */}
                <Route
                  path="/customers"
                  element={withLayout(IndividualClients)()}
                />
                {/* Inventory Management */}
                <Route
                  path="/inventory"
                  element={withLayout(InventoryTable)()}
                />
                <Route
                  path="/uploads"
                  element={withLayout(InventoryBulkUpload)()}
                />
                <Route
                  path="/warehouseinventory"
                  element={withLayout(WarehouseInventoryTable)()}
                />
                <Route
                  path="/warehousedistribution"
                  element={withLayout(WarehouseDistributionTable)()}
                />
                <Route
                  path="/retail-distribution-logs"
                  element={withLayout(RetailDistributionTable)()}
                />
                <Route path="/product" element={withLayout(ProductsTable)()} />
                <Route
                  path="/selling-price-rules"
                  element={withLayout(SellingPriceRules)()}
                />
                <Route
                  path="/accounts-setups"
                  element={withLayout(AccountAndFinanceModule)()}
                />
                <Route path="/pos-setups" element={withLayout(Setups)()} />
                <Route
                  path="/businessRules"
                  element={withLayout(BusinessRules)()}
                />

                <Route
                  path="/pos-salesorders"
                  element={withLayout(SalesOrderTable)()}
                />

                <Route
                  path="/pos-reverse-salesorders"
                  element={withLayout(ReversedSaleorder)()}
                />
                <Route
                  path="/payment-voucher"
                  element={withLayout(PaymentVoucher)()}
                />
                <Route
                  path="/pos-purchaseorders"
                  element={withLayout(PurchaseOrderTable)()}
                />
                <Route
                  path="/pos-reverse-purchaseorders"
                  element={withLayout(ReversePurchaseOrders)()}
                />
                <Route
                  path="/pos-external-products"
                  element={withLayout(ExternalProductsTable)()}
                />
                <Route
                  path="/pos-sales-orders-items"
                  element={withLayout(SalesOrderItemsTable)()}
                />
                <Route
                  path="/pos-sales-payment-methods"
                  element={withLayout(SaleOrderPaymentMethod)()}
                />
                <Route
                  path="/pos-purchase-payment-methods"
                  element={withLayout(PurchaseOrderPaymentMethod)()}
                />
                <Route
                  path="/pos-purchase-orders-items"
                  element={withLayout(PurchaseOrderItemsTable)()}
                />
              </Routes>
            </ApolloProvider>
            <ApolloProvider client={userManagementClient}>
              <Routes>
                <Route path="/users" element={withLayout(Users)()} />
                <Route path="/usersform" element={withLayout(UsersForm)()} />
                <Route path="/password-policy" element={withLayout(PasswordPolicy)()} />
              </Routes>
            </ApolloProvider>

            <ApolloProvider client={setupManagementClient}>
              <Routes>
                {/* <Route path="/Lead" element={withLayout(LeadSetups)()} />
                <Route
                  path="/Geography"
                  element={withLayout(GeographySetups)()}
                />
                <Route
                  path="/othersetups"
                  element={withLayout(OtherSetups)()}
                /> */}
                <Route
                  path="/chart-of-accounts"
                  element={withLayout(ChartOfAccounts)()}
                />
                <Route
                  path="/inventory-branch-transfer"
                  element={withLayout(InventoryBranchTransfer)()}
                />
                <Route
                  path="/suspendedtransactions"
                  element={withLayout(SuspendedTransactions)()}
                />
                <Route
                  path="/manage-companies"
                  element={withLayout(CompanyManagementModule)()}
                />
                <Route
                  path="/butcheryPOS"
                  //element={withLayout(ButcheryPOS)()}
                  element={withLayout(ShoeInventoryPOS)()}
                />
                <Route path="/hotelPOS" element={withLayout(HotelPOS)()} />
                <Route path="/expenses" element={withLayout(Expense)()} />
                <Route path="/adhoc" element={withLayout(AdhocMain)()} />

                <Route
                  path="/fixed-expenses"
                  element={withLayout(FixedExpenses)()}
                />
                {/* <Route
                  path="/promotions"
                  element={withLayout(PromotionsPage)()}
                />
                <Route path="/message" element={withLayout(CustomSMSPage)()} />
                <Route path="/emails" element={withLayout(EmailsPage)()} /> */}

                <Route
                  path="/pos-vendors"
                  element={withLayout(VendorsTable)()}
                />
                <Route
                  path="/users-audit"
                  element={withLayout(UserAuditsTable)()}
                />
                <Route
                  path="/inventory-audits"
                  element={withLayout(InventoryAuditsTable)()}
                />
                 <Route
                  path="/warehouse-audits"
                  element={withLayout(WarehouseAuditsTable)()}
                />
                 <Route
                  path="/pv-audits"
                  element={withLayout(PaymentVoucherAuditsTable)()}
                />
                 <Route
                  path="/sales-audits"
                  element={withLayout(SalesOrdersAuditsTable)()}
                />
                 <Route
                  path="/salesitems-audits"
                  element={withLayout(SalesOrderItemsAuditsTable)()}
                />
                 <Route
                  path="/inventory-audits"
                  element={withLayout(InventoryAuditsTable)()}
                />
                 <Route
                  path="/purchase-audits"
                  element={withLayout(PurchaseOrdersAuditsTable)()}
                />
                 <Route
                  path="/purchaseitems-audits"
                  element={withLayout(PurchaseOrderItemsAuditsTable)()}
                />
                {/* <Route
                  path="/messagetemplates"
                  element={withLayout(MessageTemplates)()}
                />
                <Route
                  path="/systemsetups"
                  element={withLayout(SystemSetups)()}
                /> */}
              </Routes>
            </ApolloProvider>

            <Routes>
              <Route
                path="/reports-major-classification"
                element={withLayout(MajorClassification)()}
              />
              <Route
                path="/reports-minor-classification"
                element={withLayout(MinorClassification)()}
              />
              <Route path="/report-types" element={withLayout(ReportTypes)()} />
              <Route
                path="/report-browser"
                element={withLayout(ReportBrowser)()}
              />

              {/* <Route path="/adhoc" element={withLayout(AdhocMain)()} /> */}
            </Routes>
          </>
        )}
      </ThemeProvider>
      {/* </Provider> */}
      <EnvironmentIndicator />
    </ColorModeContext.Provider>
  );
}

export default App;